<template>
  <v-card>
    <div class="ration-editor flex">
      <div class="ration-sidebar">
        <v-card-title>
          <h2 class="card-title">{{ isEditMode ? $t('base.edit_ration') : $t('base.add_ration') }}</h2>
        </v-card-title>

        <v-card-title>
          <div class="" style="margin-bottom: 2rem; margin-top: 1rem;">
              <v-input-r :label="$t('base.name')" v-model="form.name" />
          </div>
        </v-card-title>

          <div class="stocks" ref="stocksContainer">
            <div class="stocks-title">{{ 'base.stock' | translate }}</div>
            <div class="stocks-search">
              <ui-search-input
                v-model="stockSearch"
                :placeholder="$t('base.search')"
                class="search-component"
              />
            </div>
            <div v-if="filteredStocks.length" class="stocks-custom-tabs">
            <ul class="v-tabs v-tabs--vertical">
              <v-tab
                v-for="stock in filteredStocks"
                :key="stock.id"
                :active="selectedStock && selectedStock.id === stock.id"
                :class="{ 'v-tab--used': stock.isUsedInRation }"
                @click="onStockSelect(stock)"
              >
                <div class="stock-tab-content">
                  <span class="stock-name">{{ stock.name | translate }}</span>
                </div>
              </v-tab>
            </ul>
          </div>
          <div v-if="stocksLoading" class="stocks-loading">
            <div class="loading-spinner"></div>
          </div>
          <div v-if="!stocksLoading && !filteredStocks.length" class="stocks-empty">
            {{ 'base.no_options' | translate }}
          </div>
          <div v-if="!stocksHasMore && filteredStocks.length > 0" class="stocks-end">
            <i class="i-util-check"></i>
            <span>{{ $t('base.all_stocks_loaded') }}</span>
          </div>
          <div v-if="filteredStocks.length > 0" class="stocks-stats">
            <small>
              {{ 'base.showing' | translate }} {{ allLoadedStocks.length }} 
              {{ 'base.of' | translate }} {{ totalStocksCount }} 
              {{ 'base.stocks_list' | translate }}
            </small>
          </div>
        </div>

        <v-card-actions>
          <v-btn class="mr-2 ration-submit-btn" :disabled="!isFormValid" :loading="progress" @click="onSubmit">
            {{ isEditMode ? $t('base.save') : $t('base.add_ration') }}
          </v-btn>
          <delete-btn class="ration-delete-btn" v-if="isEditMode" @delete="onDelete"></delete-btn>
        </v-card-actions>
      </div>
<div class="ration-content">
    <div class="ration-content-header">
          <table class="table ration-content-header-one">
            <thead class="table__header">
              <tr class="table__row table__row--head">
                <th class="table__cell">{{ 'base.ingredient' | translate }}</th>
                <th class="table__cell" v-if="lang === 'uk'">
                  <div class="two-cols">
                    <div>{{ 'base.weight_cm' | translate }}</div> 
                    <div>{{ 'base.dry_metter_cp' | translate }}</div>
                  </div>
                </th>
                <th class="table__cell" v-else>
                  <div class="two-cols">
                    <div>{{ 'base.dry_metter_cp' | translate }}</div>
                    <div>{{ 'base.weight_cm' | translate }}</div> 
                  </div>
                </th>
                <th class="table__cell">{{ 'base.dry_metter' | translate }} (%)</th>
                <th class="table__cell">{{ 'base.humidity' | translate }}</th>
              </tr>
            </thead>
          </table>
          <table class="table ration-content-header-two">
            <template v-if="loaded">
              <tr class="table__row">
                <td class="table__cell"></td>
                <td class="table__cell" v-if="lang === 'uk'">
                  <div class="two-cols two-cols-header">
                    <div @click="setIsDry(form, false)" :class="getDryToggleClass(false)">
                      <span class="ration-selected-item">
                        <span class="ration-selected-item-count">{{ formatNumber(totalWeight) }}</span>
                      </span>
                      <span>{{ 'base.kg' | translate }}</span>
                    </div>
                    <div @click="setIsDry(form, true)" :class="getDryToggleClass(true)">
                      <span class="ration-selected-item">
                        <span class="ration-selected-item-count">{{ formatNumber(totalDryWeight) }}</span>
                      </span>
                      <span> {{ 'base.kg' | translate }}</span>
                    </div>
                  </div>
                </td>
                <td class="table__cell" v-else>
                  <div class="two-cols two-cols-header">
                    <div @click="setIsDry(form, true)" :class="getDryToggleClass(true)">
                      <span class="ration-selected-item">
                        <span class="ration-selected-item-count">{{ formatNumber(totalDryWeight) }}</span>
                      </span>
                      <span> {{ 'base.kg' | translate }}</span>
                    </div>
                    <div @click="setIsDry(form, false)" :class="getDryToggleClass(false)">
                      <span class="ration-selected-item">
                        <span class="ration-selected-item-count">{{ formatNumber(totalWeight) }}</span>
                      </span>
                      <span>{{ 'base.kg' | translate }}</span>
                    </div>
                  </div>
                </td>
                <td class="table__cell">{{ formatNumber(rationDryMatter()) }} %</td>
                <td class="table__cell">{{ formatNumber(rationHumidity()) }} %</td>
              </tr>
            </template>
          </table>
    </div>
    <div class="radion-ingridients-scroll">
      <div class="radion-ingridients">
        <div class="ingredients">
            <table class="table">
              <template v-if="loaded">
                <draggable class="table__body" tag="tbody" handle=".cursor-move" v-model="sortedIngredients">
                  <tr class="table__row" v-for="(item, index) in sortedIngredients" :key="item.id || index">
                    <td class="table__cell" :class="getCellClass(item)">
                      <div class="flex align-center">
                        <i class="i-util-bars cursor-move mr-3"></i>
                        <v-checkbox :label="item.name" :value="true" :disabled="isH2OIngredient(item) || item.is_mandatory"
                          :class="{ 'h2o-checkbox': isH2OIngredient(item) }"
                          @input="onToggleSelectedIngredient(item, $event)">
                        </v-checkbox>
                      </div>
                    </td>
                    <td class="table__cell" :class="getCellClass(item)">
                      <div class="two-cols" v-if="lang === 'uk'">
                        <div class="ration-weight-item-wrap">
                          <span v-if="!form.is_dry || isH2OIngredient(item)" class="ration-weight-item">
                            <input class="ration-weight-item-input input" type="number" step="0.01" :disabled="readOnly"
                              v-model.number="item.weight" @input="limitDecimals(item, $event)" @blur="validateWeight(item)">
                          </span>
                          <span v-else class="ration-weight-item disabled-field" :data-tooltip="$t('base.enable_cm_mode')">
                            {{ formatNumber(item.weight) }}
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                        <div v-if="!isH2OIngredient(item)" class="ration-weight-item-wrap">
                          <span v-if="form.is_dry" class="ration-weight-item">
                            <input class="ration-weight-item-input input" type="number" step="0.01" min="0"
                              :disabled="readOnly || item.dry_matter === 0" :value="calculateDryWeight(item)"
                              @input="limitDecimalsDryWeight($event)" @blur="onDryWeightChange(index, $event)" />
                          </span>
                          <span v-else class="ration-weight-item disabled-field" :data-tooltip="$t('base.enable_cp_mode')">
                            {{ formatNumber(calculateDryWeight(item)) }}
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                      </div>
                      <div class="two-cols" v-else>
                        <div v-if="!isH2OIngredient(item)" class="ration-weight-item-wrap">
                          <span v-if="form.is_dry" class="ration-weight-item">
                            <input class="ration-weight-item-input input" type="number" step="0.01" min="0"
                              :disabled="readOnly || item.dry_matter === 0" :value="calculateDryWeight(item)"
                              @input="limitDecimalsDryWeight($event)" @blur="onDryWeightChange(index, $event)" />
                          </span>
                          <span v-else class="ration-weight-item disabled-field" :data-tooltip="$t('base.enable_cp_mode')">
                            {{ formatNumber(calculateDryWeight(item)) }}
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                        <div class="ration-weight-item-wrap">
                          <span v-if="!form.is_dry || isH2OIngredient(item)" class="ration-weight-item">
                            <input class="ration-weight-item-input input" type="number" step="0.01" :disabled="readOnly"
                              v-model.number="item.weight" @input="limitDecimals(item, $event)" @blur="validateWeight(item)">
                          </span>
                          <span v-else class="ration-weight-item disabled-field" :data-tooltip="$t('base.enable_cm_mode')">
                            {{ formatNumber(item.weight) }}
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="table__cell" :class="getCellClass(item)">
                      {{ formatDryMatterPercentage(item) }}
                    </td>
                    <td class="table__cell" :class="getCellClass(item)">
                    </td>
                  </tr>
                </draggable>

                <tbody class="table__body">
                  <tr class="table__row" v-for="item in availableIngredients" :key="item.id">
                    <td class="table__cell" style="padding-left: 5rem;">
                      <v-checkbox :label="item.name" :value="false" @input="onSelectAvailableIngredient(item, $event)">
                      </v-checkbox>
                    </td>
                    <td class="table__cell">
                      <div class="two-cols" v-if="lang === 'uk'">
                        <div class="ration-selected-item-wrap fake">
                          <span class="ration-selected-item">
                            <span class="ration-selected-item-count">0</span>
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                        <div class="ration-selected-item-wrap fake">
                          <span class="ration-selected-item">
                            <span class="ration-selected-item-count">0</span>
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                      </div>
                      <div class="two-cols" v-else>
                        <div class="ration-selected-item-wrap fake">
                          <span class="ration-selected-item">
                            <span class="ration-selected-item-count">0</span>
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                        <div class="ration-selected-item-wrap fake">
                          <span class="ration-selected-item">
                            <span class="ration-selected-item-count">0</span>
                          </span>
                          <span> {{ 'base.kg' | translate }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="table__cell" style="opacity: 0.3">
                      {{ formatDryMatterPercentage(item) }}
                    </td>
                    <td class="table__cell">
                    </td>
                  </tr>
                </tbody>
              </template>
              <v-table-body v-else loading :cols="4"></v-table-body>
            </table>
        </div>
      </div>
      </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import api from '@/api'
import Draggable from 'vuedraggable'
import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'
import UiSearchInput from '@/components/ui/ui-search-input/UiSearchInput'

export default {
  name: 'DetailModal',
  components: {
    Draggable,
    DeleteBtn,
    UiSearchInput
  },

  props: {
    selected: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      form: {
        name: null,
        is_dry: false,
        ingredients: []
      },
      options: {
        stocks: [],
        ingredients: []
      },
      selectedStock: null,
      stockSearch: '',
      stockSearchTimeout: null,
      stockSearchRequestId: 0,
      loaded: false,
      progress: false,
      h2oManuallyMoved: false,
      stocksLoading: false,
      stocksHasMore: true,
      stocksOffset: 0,
      stocksLimit: 100,
      allLoadedStocks: [],
      totalStocksCount: 0
    }
  },

  computed: {
    allowView () {
      return this.hasPermission('ration', 'view')
    },

    readOnly () {
      return !this.hasPermission('ration', 'change')
    },

    isEditMode () {
      return this.selected !== null
    },

    lang () {
      return this.$store.getters['account/lang']
    },

    totalWeight () {
      const sum = this.form.ingredients
        .reduce((sum, item) => {
          const weight = parseFloat(item.weight) || 0
          return sum + weight
        }, 0)
      return (Math.round(sum * 100) / 100).toFixed(2)
    },

    totalDryWeight () {
      const sum = this.form.ingredients
        .reduce((sum, item) => {
          const weight = parseFloat(item.weight) || 0
          const dm = parseFloat(item.dry_matter) || 0
          const normalizedDm = dm > 100 ? dm / 10000 : dm / 100
          return sum + (weight * normalizedDm)
        }, 0)
      return (Math.round(sum * 100) / 100).toFixed(2)
    },

    filteredStocks () {
      const farmId = Number(this.$route.params.farm_id)
      return this.allLoadedStocks
        .filter(stock =>
          (stock.farm && stock.farm.id === farmId) || stock.farm === null
        )
        .map(stock => ({
          ...stock,
          isUsedInRation: this.usedStockIds.has(stock.id)
        }))
    },
    usedStockIds () {
      const stockIds = new Set()
      this.form.ingredients.forEach(ingredient => {
        if (ingredient.stock && ingredient.stock.id) {
          stockIds.add(ingredient.stock.id)
        }
      })
      return stockIds
    },
    availableIngredients () {
      if (!this.selectedStock) return []

      const selectedIds = new Set(this.form.ingredients.map(item => item.id))

      return this.options.ingredients.filter(ingredient =>
        ingredient.stock &&
        ingredient.stock.id === this.selectedStock.id &&
        !selectedIds.has(ingredient.id) &&
        !this.isH2OIngredient(ingredient)
      )
    },

    hasInvalidIngredients () {
      return this.form.ingredients.some(item => {
        const weight = parseFloat(item.weight)
        const dm = parseFloat(item.dry_matter)
        return (
          weight < 0 || isNaN(weight) ||
          (!this.isH2OIngredient(item) && (dm < 0 || isNaN(dm)))
        )
      })
    },

    isFormValid () {
      return (
        this.form.name &&
        this.form.name.length > 0 &&
        this.form.ingredients.length > 0 &&
        !this.hasInvalidIngredients
      )
    },

    farmId () {
      return this.$route.params.farm_id
    },

    sortedIngredients: {
      get () {
        if (this.h2oManuallyMoved) {
          return this.form.ingredients
        }

        const h2oIngredients = this.form.ingredients.filter(item => this.isH2OIngredient(item))
        const otherIngredients = this.form.ingredients.filter(item => !this.isH2OIngredient(item))

        return [...otherIngredients, ...h2oIngredients]
      },
      set (value) {
        const oldH2OPositions = this.form.ingredients
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => this.isH2OIngredient(item))
          .map(({ index }) => index)

        const newH2OPositions = value
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => this.isH2OIngredient(item))
          .map(({ index }) => index)

        if (JSON.stringify(oldH2OPositions) !== JSON.stringify(newH2OPositions)) {
          this.h2oManuallyMoved = true
        }
        this.form.ingredients = value
      }
    }
  },

  methods: {
    formatNumber (value) {
      if (value == null || value === '') return value
      const num = parseFloat(value)
      if (isNaN(num)) return value

      const rounded = Math.round(num * 100) / 100
      const fixed = rounded.toFixed(2)

      return String(fixed).replace('.', ',')
    },

    limitDecimals (ingredient, event) {
      const value = event.target.value
      if (value && value.includes('.')) {
        const parts = value.split('.')
        if (parts[1] && parts[1].length > 2) {
          const limitedValue = parseFloat(parts[0] + '.' + parts[1].substring(0, 2))
          ingredient.weight = limitedValue
          event.target.value = limitedValue.toFixed(2)
        }
      }
    },

    limitDecimalsDryWeight (event) {
      const value = event.target.value
      if (value && value.includes('.')) {
        const parts = value.split('.')
        if (parts[1] && parts[1].length > 2) {
          const limitedValue = parseFloat(parts[0] + '.' + parts[1].substring(0, 2))
          event.target.value = limitedValue.toFixed(2)
        }
      }
    },

    isH2OIngredient (item) {
      return item.name === 'H2O'
    },

    getCellClass (item) {
      if (this.isH2OIngredient(item)) return 'bg-h2o'
      if (this.isInSelectedStock(item)) return 'bg-new-green'
      return 'bg-h2o'
    },

    isInSelectedStock (item) {
      return item.stock &&
        this.selectedStock &&
        item.stock.id === this.selectedStock.id
    },

    getDryToggleClass (isDry) {
      return [
        'ration-selected-item-wrap',
        this.form.is_dry === isDry ? 'selected' : 'cursor-pointer'
      ]
    },

    onSelectAvailableIngredient (item, isSelected) {
      if (!isSelected) return

      const newIngredient = {
        id: item.id,
        name: item.name,
        dry_matter: item.dry_matter,
        weight: Math.round(parseFloat(item.weight || 0) * 100) / 100,
        selected: true,
        stock: item.stock
      }

      if (this.h2oManuallyMoved || this.isH2OIngredient(item)) {
        this.form.ingredients.push(newIngredient)
      } else {
        const h2oIndex = this.form.ingredients.findIndex(ing => this.isH2OIngredient(ing))
        if (h2oIndex !== -1) {
          this.form.ingredients.splice(h2oIndex, 0, newIngredient)
        } else {
          this.form.ingredients.push(newIngredient)
        }
      }
    },

    onToggleSelectedIngredient (item, isSelected) {
      if (this.isH2OIngredient(item)) return
      if (item.is_mandatory) return
      if (!isSelected) {
        this.form.ingredients = this.form.ingredients.filter(
          ingredient => ingredient.id !== item.id
        )
      }
    },

    setIsDry (ration, value) {
      ration.is_dry = value
    },

    validateWeight (item) {
      const weight = parseFloat(item.weight)
      if (isNaN(weight) || weight < 0) {
        item.weight = 0
      } else {
        item.weight = Math.round(weight * 100) / 100
      }
    },

    onDryWeightChange (index, event) {
      const currentIngredients = this.h2oManuallyMoved ? this.form.ingredients : this.sortedIngredients
      const item = currentIngredients[index]
      const value = parseFloat(event.target.value)

      if (isNaN(value) || value < 0 || item.dry_matter === 0) {
        item.weight = 0
        return
      }

      const normalizedDm = item.dry_matter > 100
        ? item.dry_matter / 10000
        : item.dry_matter / 100

      const weight = value / normalizedDm

      const actualIndex = this.form.ingredients.findIndex(ing => ing.id === item.id)
      this.$set(this.form.ingredients, actualIndex, {
        ...item,
        weight: Math.round(weight * 100) / 100
      })
    },

    calculateDryWeight (ingredient) {
      const weight = parseFloat(ingredient.weight) || 0
      const dm = parseFloat(ingredient.dry_matter) || 0

      if (weight === 0 || dm === 0) return 0

      const normalizedDm = dm > 100 ? dm / 10000 : dm / 100
      const result = weight * normalizedDm

      return Math.round(result * 100) / 100
    },

    rationHumidity () {
      const weight = parseFloat(this.totalWeight)
      const dry = parseFloat(this.totalDryWeight)
      if (weight === 0) return 0
      const humidity = ((weight - dry) / weight) * 100
      return Math.round(humidity * 100) / 100
    },

    rationDryMatter () {
      const humidity = parseFloat(this.rationHumidity())
      const dryMatter = 100 - humidity
      return Math.round(dryMatter * 100) / 100
    },

    formatDryMatterPercentage (item) {
      if (this.isH2OIngredient(item)) return ''

      const dm = parseFloat(item.dry_matter) || 0
      const percentage = item.is_animalfeed ? dm / 100 : dm
      return `${percentage.toFixed(2)}%`
    },

    async onSubmit () {
      this.progress = true

      const ingredientsToSave = this.h2oManuallyMoved ? this.form.ingredients : this.sortedIngredients
      const ingredients = ingredientsToSave.map((item, index) => ({
        id: item.id,
        name: item.name,
        dry_matter: item.dry_matter,
        weight: item.weight || 0,
        ordering: index
      }))

      const payload = {
        name: this.form.name,
        full_name: '',
        farm: this.farmId,
        ingredients,
        is_dry: this.form.is_dry
      }

      try {
        const result = this.isEditMode
          ? await api.rations.updateById(this.selected.id, payload)
          : await api.rations.create(payload)

        if (result.error) {
          this.$toasted.error(this.$t('errors.error_try_again'))
        } else {
          this.$emit('close')
          this.$emit('update')
        }
      } catch (error) {
        this.$toasted.error(this.$t('errors.error_try_again'))
      }

      this.progress = false
    },

    async onDelete () {
      try {
        await api.rations.deleteById(this.selected.id)
        this.$emit('close')
        this.$emit('update')
      } catch (error) {
        this.$toasted.error(this.$t('errors.error_try_again'))
      }
    },

    async loadStocks (offset = 0, append = false) {
      const company = +this.$route.params.company_id
      const farm = +this.$route.params.farm_id

      if (this.stocksLoading || !this.stocksHasMore) return

      this.stocksLoading = true
      const requestId = this.stockSearchRequestId

      try {
        const params = {
          company,
          farm,
          offset,
          limit: this.stocksLimit
        }
        const search = this.stockSearch.trim()
        if (search) params.name = search
        const response = await api.stocks.list(params)
        if (requestId !== this.stockSearchRequestId) return

        const newStocks = response.data || []
        this.totalStocksCount = response.count || 0

        if (append) {
          const existingIds = new Set(this.allLoadedStocks.map(s => s.id))
          const uniqueNewStocks = newStocks.filter(s => !existingIds.has(s.id))

          if (uniqueNewStocks.length === 0) {
            this.stocksHasMore = false
            this.stocksLoading = false
            return
          }

          this.allLoadedStocks = [...this.allLoadedStocks, ...uniqueNewStocks]
        } else {
          this.allLoadedStocks = newStocks

          if (!this.selectedStock && newStocks.length > 0) {
            this.$nextTick(() => {
              const filtered = this.filteredStocks
              if (filtered.length > 0) {
                const defaultStock = filtered.find(stock => stock.is_default)
                this.selectedStock = defaultStock || filtered[0]

                this.scrollToSelectedStock()
              }
            })
          }
        }

        const hasNext = response.next !== null
        const hasMoreByCount = this.allLoadedStocks.length < this.totalStocksCount
        const gotFullPage = newStocks.length === this.stocksLimit

        this.stocksHasMore = hasNext && hasMoreByCount && gotFullPage

        this.stocksOffset = offset
        this.options.stocks = this.allLoadedStocks
      } catch (error) {
        if (requestId !== this.stockSearchRequestId) return
        console.error('Error loading stocks:', error)
        this.stocksHasMore = false
      } finally {
        if (requestId === this.stockSearchRequestId) this.stocksLoading = false
      }
    },

    async loadMoreStocks () {
      if (!this.stocksHasMore || this.stocksLoading) return

      const nextOffset = this.stocksOffset + this.stocksLimit
      await this.loadStocks(nextOffset, true)
    },

    onStocksScroll (event) {
      if (!this.stocksHasMore || this.allLoadedStocks.length >= this.totalStocksCount) {
        return
      }

      const element = event.target
      const threshold = 50

      if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
        this.loadMoreStocks()
      }
    },

    onStockSelect (stock) {
      this.selectedStock = stock
      this.scrollToSelectedStock()
    },

    scrollToSelectedStock () {
      this.$nextTick(() => {
        if (!this.selectedStock) return

        const stocksContainer = this.$refs.stocksContainer
        const activeTab = this.$el.querySelector('.v-tab.v-tab--active')

        if (stocksContainer && activeTab) {
          const scrollTop = activeTab.offsetTop - (stocksContainer.clientHeight / 1.8) + (activeTab.clientHeight / 1.8)

          stocksContainer.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          })
        }
      })
    },

    async loadIngredients () {
      try {
        const response = await api.ingredients.litelist({ limit: 1000, farm: this.farmId })
        this.options.ingredients = response.data
      } catch (error) {
        console.error('Error loading ingredients:', error)
      }
    },

    async loadData () {
      await Promise.all([
        this.loadStocks(),
        this.loadIngredients()
      ])

      this.ensureH2OExists()
      this.loaded = true
    },

    ensureH2OExists () {
      const defaultStock = this.filteredStocks.find(stock => stock.is_default)
      if (!defaultStock) return

      const existingIds = new Set(this.form.ingredients.map(item => item.id))

      const mandatoryIngredients = this.options.ingredients
        .filter(ing =>
          ing.is_mandatory &&
          ing.stock.id === defaultStock.id &&
          !existingIds.has(ing.id)
        )
        .map(ing => ({
          ...ing,
          selected: true,
          weight: Math.round(parseFloat(ing.weight || 0) * 100) / 100
        }))

      if (mandatoryIngredients.length > 0) {
        this.form.ingredients = [...this.form.ingredients, ...mandatoryIngredients]
      }
    }
  },

  watch: {
    stockSearch () {
      clearTimeout(this.stockSearchTimeout)
      this.stockSearchRequestId++
      this.allLoadedStocks = []
      this.options.stocks = []
      this.totalStocksCount = 0
      this.stocksOffset = 0
      this.stocksHasMore = true
      this.stocksLoading = true
      this.stockSearchTimeout = setTimeout(() => {
        this.stocksLoading = false
        this.loadStocks()
      }, 300)
    }
  },

  async mounted () {
    if (this.selected) {
      this.form.name = this.selected.name
      this.form.is_dry = this.selected.is_dry !== undefined ? this.selected.is_dry : false
      this.form.ingredients = this.selected.ingredients.map(item => ({
        ...item,
        selected: true,
        weight: Math.round(parseFloat(item.weight || 0) * 100) / 100
      }))

      const lastIngredient = this.form.ingredients[this.form.ingredients.length - 1]
      if (lastIngredient && this.isH2OIngredient(lastIngredient)) {
        this.h2oManuallyMoved = false
      } else {
        this.h2oManuallyMoved = true
      }
    }

    await this.loadData()

    this.$nextTick(() => {
      const stocksContainer = this.$refs.stocksContainer
      if (stocksContainer) {
        stocksContainer.addEventListener('scroll', this.onStocksScroll)
      }
    })
  },

  beforeDestroy () {
    clearTimeout(this.stockSearchTimeout)
    this.stockSearchRequestId++
    const stocksContainer = this.$refs.stocksContainer
    if (stocksContainer) {
      stocksContainer.removeEventListener('scroll', this.onStocksScroll)
    }
  }
}
</script>

<style lang="scss">
@import '@/sass/abstracts/_variables.scss';

.v-tabs--vertical {
  flex: 1;
}

.ration-editor {

  .table__cell {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }
  min-height: 85vh;

  .ration-sidebar {
    width: 100%;
    max-width: 28rem;
    display: flex;
    flex-direction: column;
    border-right: 1px solid #D7D7D7;
  }

  .ration-content {
    width: 100%;
  }

  .card-title {
    font-size: 2.4rem;
  }

  .ration-submit-btn {
    width: 100%;
  }

  .ration-delete-btn {
    margin-top: 2rem;
    width: 100%;

    .v-dropdown__toggler,
    .v-dropdown {
      width: 100%;
    }

    button {
      width: 100%;
    }

    .v-dropdown__content {
      top: -21rem;
    }
  }

  .delete-btn__content {
    min-width: 23rem;
    max-width: 23rem;

    .v-card--elevated {
      .v-btn--default {
        margin-top: 1rem;
      }
    }
  }

  .stocks {
    border-top: 1px solid #D7D7D7;
    border-bottom: 1px solid #D7D7D7;
    padding: 0 2.4rem;
    flex: 1;
    margin-bottom: 2rem;
    max-height: 57vh;
    overflow: auto;
    display: flex;
    flex-direction: column;

    .stocks-title {
      font-size: 1.4rem;
      font-weight: 500;
      margin-top: 1rem;
      color: #979797;
      margin-bottom: -1rem;
    }

    .stocks-custom-tabs {
      flex: 1;
    }

    .v-tab--used {
      position: relative;
      width: 100%;
      border-left: 5px solid #4CAF50;
    }
  }

  .stocks-search {
    margin: 2rem 0 1.2rem;
    flex-shrink: 0;

    .search-component {
      width: 100%;
      max-width: none;
      min-width: 0;
    }
  }

  .stocks-empty {
    padding: 2rem 0;
    color: #979797;
    font-size: 1.4rem;
  }

  .stocks-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
    color: #979797;
    font-size: 1.4rem;

    .loading-spinner {
      width: 2rem;
      height: 2rem;
      border: 2px solid #f3f3f3;
      border-top: 2px solid #59CC56;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  .stocks-end {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 1rem;
    color: #4CAF50;
    font-size: 1.2rem;

    i {
      font-size: 1.4rem;
    }
  }

  .stocks-stats {
    padding: 1rem 0 0.5rem;
    text-align: center;
    border-top: 1px solid #f0f0f0;

    small {
      color: #979797;
      font-size: 1.1rem;
    }
  }

  .ration-content-header {
    padding-right: 2rem;
    padding-left: 2rem;
    .ration-content-header-one,
    .ration-content-header-two {
      padding: 0 2rem;
      border-spacing: 0 !important;
      width: 100%;
      table-layout: fixed;

      .table__cell {
        padding-top: 1.2rem;
        padding-bottom: 1.2rem;

        &:nth-child(1) {
          width: 22rem;
          min-width: 22rem;
        }

        &:nth-child(2) {
          width: calc(100% - 22rem - 20rem - 20rem);
          min-width: 220px;
        }

        &:nth-child(3) {
          width: 20rem;
          min-width: 20rem;
        }

        &:nth-child(4) {
          width: 20rem;
          min-width: 20rem;
        }
      }
    }

    .ration-content-header-one {
      border-bottom: 1px solid #BDEDCA;
    }

    .ration-content-header-two {
      background-color: #F5F5F5;
      border-radius: 1rem;
      .table__cell {
        padding-bottom: 0;
      }
    }
  }

  .two-cols {
    display: flex;
    align-items: center;
    gap: 1.6rem;
    justify-content: space-between;
    width: 100%;
    padding: 0.4rem;

  }

  .two-cols-header {
    background: #EEEEEE;
    border-radius: 1rem;
 
  }

  .radion-ingridients-scroll {
    max-height: 70vh;
    overflow-y: auto;
    padding: 2rem;
    padding-top: 0;
    padding-right: 1rem;
    flex: 1;
    scrollbar-width: thin;
    scrollbar-color: #59CC56 transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 3px;
      border: 1px solid transparent;
    }

    &:hover::-webkit-scrollbar-thumb {
      background-color: #555;
    }
  }

  .radion-ingridients {
    width: 100%;

    .table {
      border-spacing: 0 1rem;
      border-collapse: separate;
      table-layout: fixed;
      width: 100%;

      .table__row {
        border: none !important;
      }

      .table__cell {
        &:first-child {
          border-top-left-radius: 1rem;
          border-bottom-left-radius: 1rem;
        }

        &:last-child {
          border-top-right-radius: 1rem;
          border-bottom-right-radius: 1rem;
        }

        &:nth-child(1) {
          width: 22rem;
          min-width: 22rem;
          padding-right: 0;
        }

         &:nth-child(2) {
          width: calc(100% - 22rem - 20rem - 20rem);
          min-width: 220px;
        }

        &:nth-child(3) {
          width: 20rem;
          min-width: 20rem;
        }

        &:nth-child(4) {
          width: 20rem;
          min-width: 20rem;
        }
      }

      .v-checkbox__label {
        word-break: break-all;
      }
    }
  }

  .ration-selected-item-wrap {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: #eee;
    justify-content: center;
    overflow: hidden;
    width: 130px;

    .ration-selected-item {
      padding: 0.8rem;
      border-radius: 0.8rem;
      font-weight: 900;
      padding-right: 0;
      padding-left: 0;
      text-align: center;
    }
    &.selected {
      background: #59CC56;
      border-radius: 8px;
      color: white;
      font-size: 16px;
      font-weight: 900;
      padding: 0 1.8rem;
      justify-content: center;
    }
    &.selected .ration-selected-item {
      // background-color: #BDEDCA;
    }

    &.fake {
      opacity: 0.3;
      pointer-events: none;
      background: unset;

      .ration-selected-item {
        background-color: #EEE;
        width: 60px;
      }
    }
  }

  .ration-weight-item {
    display: block;
    padding: 0.8rem;
    border-radius: 0.8rem;
    padding-right: 0;
    padding-left: 0;

    text-align: center;

    &:has(input) {
      padding: 0;
    }
    
    &.disabled-field {
      cursor: help;
    }
  }

  .ration-weight-item-wrap {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 118px;
    justify-content: center;
    position: relative;

    .disabled-field {
      &:hover::before {
        content: attr(data-tooltip);
        position: absolute;
        top: -50%;
        left: -100px;
        background-color: #ffe8f0;
        color: #ff6b9d;
        padding: 0.8rem 1.6rem;
        border-radius: 2rem;
        font-size: 1.3rem;
        font-weight: 500;
        white-space: nowrap;
        z-index: 1000;
      }
    }

    .ration-weight-item-input {
      background-color: #FEFEFE;
      padding: 0.8rem;
      border-radius: 0.5rem;
      font-weight: 500;
      
      text-align: center;
      &:focus {
    background: #ddf2e3;
}

      &:disabled {
        background-color: gainsboro;
        cursor: not-allowed;
      }
    }
  }

  .input {
    width: auto;
    max-width: 6rem;
    padding: 0.4rem;
    text-align: right;
    background-color: #f8f8f8;
    border-bottom: 1px solid #e2e2e2;
  }

  .bg-h2o {
    background-color: #f5f5f5 !important;
  }

  .bg-new-green {
    background-color: #BDEDCA !important;
  }

  .bg-white {
    background-color: #fff !important;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
